import { toast } from 'react-toastify'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { Tab } from 'features/declaration/form/components/editorTabs'
import { ErrorResponse, ParsedErrorResponse, parseErrorResponse } from 'shared/api/parseAxiosTransitErrorResponse'
import { isAxiosError } from 'shared/api/isAxiosError'
import ExchangeRateUnavailableError from 'shared/currency-exchange-rate/models/ExchangeRateUnavailableError'
import notifyExchangeRateError from 'shared/currency-exchange-rate/components/notifyExchangeRateError'
import { mapRejectionReasonToTranslationString } from '../../../../helpers/errors.helper'
import { NctsError } from '../../../../shared/ncts/types'
import { useNctsErrors } from '../contexts/NctsErrorContext'
import { BadRequestResponse } from '../../../../types/Errors'
import { isApiError } from '../../../../errors/ApiError'

function fieldErrorToNctsError(fieldError: { message: string; field: string }): NctsError {
  return {
    message: fieldError.message,
    path: fieldError.field,
  }
}

interface UseTransitApiErrorNotificationsProps {
  goToTab: (tab: Tab) => Promise<void> | void
}

function parseError(error: Error | undefined) {
  if (!error) return { status: null, errorResponse: null }

  let status: number | undefined
  let errorResponse: ParsedErrorResponse | undefined
  if (isApiError(error)) {
    status = error.status
    errorResponse = parseErrorResponse(error.details as ErrorResponse, error.status)
  }

  if (isAxiosError<ErrorResponse>(error)) {
    status = error.response?.status
    errorResponse = parseErrorResponse(error.response?.data, error.response?.status)
  }
  return {
    status,
    errorResponse,
  }
}

function useDeclarationApiErrorNotifications({
  goToTab,
}: UseTransitApiErrorNotificationsProps) {
  const { t } = useTranslation()
  const { setNctsErrors } = useNctsErrors()

  const handleTransitApiError = async (error: Error | undefined, errorReason: string) => {
    if (!error) return

    if (error instanceof ExchangeRateUnavailableError) {
      notifyExchangeRateError(error)
      return
    }

    const parsedError = parseError(error)
    const { status, errorResponse } = parsedError

    if (status === 503) {
      toast.error(
        t('common.submit', { context: 'service_unavailable' }),
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        },
      )
      return
    }

    if (status === 500 || errorReason === 'general') {
      toast.error(
        t('errors.serverError', { context: 'serverError', traceId: errorResponse?.traceId ?? null }),
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        },
      )
      return
    }

    if (status === 413) {
      toast.error(
        t('common.submit', { context: 'payload_too_large' }),
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        },
      )
      return
    }

    if (status === 409) {
      toast.error(
        errorResponse?.generalMessage ?? t('common.submit', { context: 'error' }),
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        },
      )
      return
    }

    if (errorReason === 'requiredGoodsDocumentsNotUploaded') {
      toast.error(t('messages.requiredGoodsDocumentsNotUploaded'))
    } else if (errorResponse?.isConstraintViolation) {
      if (errorResponse.constraintViolations?.length) {
        errorResponse.constraintViolations.forEach((badRequestResponse: BadRequestResponse) => {
          const translatedReason = mapRejectionReasonToTranslationString(badRequestResponse.reason)
          if (translatedReason) {
            const translatedField = t(`translations${i18n.language.toUpperCase()}:${badRequestResponse.field}`)
            toast.error(`${translatedField} ${translatedReason}`)
          }
        })
      }
      toast.error(t('common.submit', { context: 'errorWithTraceId', traceId: errorResponse?.traceId ?? null }))
    } else if (errorResponse?.isNctsError) {
      await goToTab('summary')
      if (errorResponse.nctsErrors?.length) {
        setNctsErrors(errorResponse.nctsErrors.map((nctsApiError) => fieldErrorToNctsError({
          message: nctsApiError.description,
          field: nctsApiError.field,
        })))
      }
      toast.error(t('common.submit', { context: 'errorWithTraceId', traceId: errorResponse?.traceId ?? null }))
    } else {
      toast.error(errorResponse?.generalMessage ?? t('common.submit', { context: 'error' }))
    }
  }

  return {
    handleTransitApiError,
  }
}

export default useDeclarationApiErrorNotifications
